import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Card, Chip, SearchField, Table } from '@heroui/react'
import { api } from '../lib/api'
import { BOOKINGS_MODULE, bookingService, lateMinutes, mayBookHere, stillToArrive, tally, valuesLine } from '../lib/bookings'
import { bookingLabel, canChange, canCopy, deskActions, linkRow, readLink } from '../lib/bookingDesk'
import { formatMoment } from '../lib/staffText'
import { explain } from '../lib/staffErrors'
import type { Appointment, BookingView, Service, Ticket, VisitField } from '../lib/types'
import { BookDrawer } from '../ui/BookDrawer'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Confirm, FormModal } from './receptionKit'
import { Qt } from './receptionStatus'

export type TodayBookings = { list: Appointment[]; today: string; load: () => Promise<void> }

/** Today's bookings at a branch, read on arrival and every minute. Reception reads
 *  them off the bookings tab too: whether any exist decides if the tab is shown. */
export function useTodayBookings(branchId: string | undefined): TodayBookings {
  const [list, setList] = useState<Appointment[]>([])
  const [today, setToday] = useState('')
  const load = useCallback(async () => {
    if (!branchId) return
    try {
      const answer = await api.appointments(branchId)
      setList(answer.appointments)
      setToday(answer.businessDate)
    } catch {
      // A list that cannot be read leaves the last one on screen. The code box
      // still works: the server finds the booking, not this list.
    }
  }, [branchId])
  useEffect(() => {
    setList([])
    setToday('')
    void load()
    const timer = window.setInterval(() => void load(), 60_000)
    return () => window.clearInterval(timer)
  }, [load])
  return { list, today, load }
}

/** The clock for Late marks, every half minute. */
function useHalfMinute(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

/** Today's bookings still to come, beside the slip on Reception's desk: Arrive issues their ticket. */
export function BookingsToday({
  branchId,
  bookings,
  declared,
  services,
  letterOf,
  zone,
  onArrived,
}: {
  branchId: string
  bookings: TodayBookings
  declared: readonly VisitField[]
  services: Service[]
  letterOf: (serviceId: string) => string
  zone?: string
  onArrived: (ticket: Ticket, booking: BookingView) => void
}) {
  const { lang, say } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const now = useHalfMinute()
  const shown = useMemo(() => stillToArrive(bookings.list, ''), [bookings.list])
  const who = (a: Appointment) => a.customerName || valuesLine(a, declared) || a.reference

  async function arrive(a: Appointment) {
    setBusy(true)
    setError('')
    try {
      const result = await api.arrive(branchId, { appointmentId: a.id })
      onArrived(result.ticket, result.appointment)
      await bookings.load()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="rx-bookings">
      <Card.Header className="rx-card-h">
        <Card.Title render={(props) => <h2 {...props} />}>{say('bookings.title')}</Card.Title>
        <Chip size="sm">{shown.length}</Chip>
      </Card.Header>
      {error && <p className="reception-error">{error}</p>}
      {shown.length === 0 ? (
        <p className="rx-empty">{say('bookings.none')}</p>
      ) : (
        <ul className="rx-blist">
          {shown.map((a) => {
            const late = lateMinutes(a.startsAt, now)
            const letter = letterOf(a.serviceId)
            return (
              <li key={a.id}>
                <span className="rx-blist__t">{formatMoment(a.startsAt, lang, { time: true, zone })}</span>
                <span className="rx-blist__who">
                  <b>{who(a)}</b>
                  <span>
                    {letter && <Qt>{letter}</Qt>}
                    {bookingService(a, services, lang) || '—'}
                    {late > 0 && (
                      <Chip size="sm" variant="soft" color="warning">
                        {say('bookings.late', { min: late })}
                      </Chip>
                    )}
                  </span>
                </span>
                <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void arrive(a)}>
                  {say('bookings.arrive')}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

/**
 * Today's bookings not yet here, found by code or name and arrived with one press;
 * a row opens Move and Cancel. Reception's Online booking tab.
 */
export function ReceptionBookings({
  branchId,
  bookings,
  modules,
  bookOnline = true,
  declared,
  services,
  onArrived,
  zone,
}: {
  branchId: string
  bookings: TodayBookings
  modules: readonly string[] | undefined
  /** The organisation's Online booking option; off hides Book. */
  bookOnline?: boolean
  declared: readonly VisitField[]
  services: Service[]
  onArrived: (ticket: Ticket, booking: BookingView) => void
  zone?: string
}) {
  const { lang, say } = useStaffText()
  const { list, today, load } = bookings
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [booking, setBooking] = useState(false)
  const [notice, setNotice] = useState('')
  // A booking opened from its row, for Move and Cancel; Move's drawer takes its place.
  const [opened, setOpened] = useState<Appointment | null>(null)
  const [moving, setMoving] = useState<Appointment | null>(null)
  const [asking, setAsking] = useState(false)
  const [drawerError, setDrawerError] = useState('')
  const [link, setLink] = useState('')
  const [linkAsked, setLinkAsked] = useState(false)
  const [copied, setCopied] = useState(false)

  const openedId = useRef<string | null>(null)
  useEffect(() => {
    openedId.current = opened?.id ?? null
    setLink('')
    setLinkAsked(false)
    setCopied(false)
  }, [opened])

  // Read on a press, not on every open: each read is on the audit trail.
  const showLink = (a: Appointment) => {
    setLinkAsked(true)
    void readLink(() => api.bookingLink(branchId, a.id)).then((r) => {
      if (openedId.current !== a.id) return
      setLinkAsked(r.asked)
      setLink(r.link)
    })
  }

  const [seated, setSeated] = useState<boolean | null>(null)
  const bought = (modules ?? []).includes(BOOKINGS_MODULE)
  useEffect(() => {
    setSeated(null)
    if (!bought) return
    api
      .heldModules(branchId)
      .then((r) => setSeated(r.held.includes(BOOKINGS_MODULE)))
      .catch(() => setSeated(null))
  }, [branchId, bought])

  const shown = useMemo(() => stillToArrive(list, query), [list, query])
  const now = useHalfMinute()
  const counts = useMemo(() => tally(list), [list])

  async function arrive(body: { appointmentId?: string; code?: string }) {
    setBusy(true)
    setError('')
    try {
      const result = await api.arrive(branchId, body)
      onArrived(result.ticket, result.appointment)
      setQuery('')
      await load()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  async function cancel(a: Appointment) {
    setBusy(true)
    setDrawerError('')
    try {
      await api.cancelBooking(branchId, a.id)
      setOpened(null)
      setNotice(say('hall.done.cancelled', { reference: bookingLabel(a) }))
      await load()
    } catch (err) {
      setDrawerError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const who = (a: Appointment) => a.customerName || valuesLine(a, declared) || a.reference
  const actions = opened ? deskActions(opened, seated !== false) : { cancel: false, move: false }

  const open = (a: Appointment) => {
    setAsking(false)
    setDrawerError('')
    setOpened(a)
  }

  return (
    <Card className="rx-book" aria-labelledby="reception-book-title">
      <Card.Header className="rx-card-h rx-book__head">
        <Card.Title id="reception-book-title" render={(props) => <h2 {...props} />}>
          {say('bookings.title')}
        </Card.Title>
        <Chip size="sm">{say('bookings.tally', { booked: counts.booked, arrived: counts.arrived })}</Chip>
        <form
          className="rx-book__find"
          onSubmit={(e) => {
            e.preventDefault()
            if (query.trim() && !busy) void arrive({ code: query })
          }}
        >
          <SearchField aria-label={say('bookings.code')} variant="secondary" value={query} onChange={setQuery} className="rx-grow">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={say('bookings.code')} autoComplete="off" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <Button type="submit" variant="secondary" size="sm" isDisabled={busy || !query.trim()}>
            {say('bookings.arriveCode')}
          </Button>
        </form>
        <span className="rx-grow" />
        {bookOnline && mayBookHere(modules, seated) && today && (
          <Button variant="primary" size="sm" onPress={() => setBooking(true)}>
            <Icon name="plus" size={15} />
            {say('booking.book')}
          </Button>
        )}
      </Card.Header>
      {error && (
        <Alert status="danger">
          <Alert.Content>
            <Alert.Title>{error}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}
      {notice && <p className="rx-note">{notice}</p>}
      {shown.length === 0 ? (
        <p className="rx-empty">{say('bookings.none')}</p>
      ) : (
        <Table variant="secondary" className="rx-table">
          <Table.ScrollContainer>
            <Table.Content aria-label={say('bookings.title')} className="rx-book__table">
              <Table.Header>
                <Table.Column className="rx-col-issued">{say('hall.time')}</Table.Column>
                <Table.Column isRowHeader>{say('reception.col.customer')}</Table.Column>
                <Table.Column>{say('hall.code')}</Table.Column>
                <Table.Column>{say('hall.service')}</Table.Column>
                <Table.Column className="rx-col-act">
                  <span className="sr">{say('bookings.arrive')}</span>
                </Table.Column>
              </Table.Header>
              <Table.Body>
                {shown.map((a) => {
                  const late = lateMinutes(a.startsAt, now)
                  return (
                    <Table.Row key={a.id} id={a.id} onAction={canChange(a) ? () => open(a) : undefined} className="rx-row">
                      <Table.Cell>
                        <span className="rx-marks">
                          <strong className="rx-num">{formatMoment(a.startsAt, lang, { time: true, zone })}</strong>
                          {late > 0 && (
                            <Chip size="sm" variant="soft" color="warning">
                              {say('bookings.late', { min: late })}
                            </Chip>
                          )}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <strong>{who(a)}</strong>
                      </Table.Cell>
                      <Table.Cell className="rx-time">{a.code}</Table.Cell>
                      <Table.Cell>{bookingService(a, services, lang) || '—'}</Table.Cell>
                      <Table.Cell className="rx-act">
                        <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void arrive({ appointmentId: a.id })}>
                          {say('bookings.arrive')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
      {booking && (
        <BookDrawer
          branchId={branchId}
          services={services}
          zone={zone}
          today={today}
          onClose={() => setBooking(false)}
          onDone={(a) => {
            setBooking(false)
            setError('')
            setNotice(say('booking.booked', { code: a.code }))
            void load().catch((err) => setError(explain(err, lang, 'common.unreachable')))
          }}
        />
      )}
      {opened && !moving && (
        <FormModal
          title={who(opened)}
          sub={[formatMoment(opened.startsAt, lang, { time: true, zone }), bookingService(opened, services, lang)].filter(Boolean).join(' · ')}
          onClose={() => setOpened(null)}
          closeLabel={say('ui.close')}
          busy={busy}
          extra={
            !asking && (
              <>
                {actions.move && (
                  <Button variant="secondary" size="sm" onPress={() => setMoving(opened)}>
                    {say('booking.move')}
                  </Button>
                )}
                {actions.cancel && (
                  <Button variant="ghost" size="sm" className="rx-danger" onPress={() => setAsking(true)}>
                    {say('appointments.cancel')}
                  </Button>
                )}
              </>
            )
          }
        >
          {drawerError && <p className="reception-error">{drawerError}</p>}
          {asking && (
            <Confirm
              question={say('appointments.cancelQuestion', { reference: bookingLabel(opened) })}
              yes={say('appointments.cancel')}
              no={say('common.keep')}
              busy={busy}
              onYes={() => void cancel(opened)}
              onNo={() => setAsking(false)}
            />
          )}
          <dl className="desk-sum">
            <div>
              <dt>{say('hall.code')}</dt>
              <dd>{opened.code}</dd>
            </div>
            <div>
              <dt>{say('hall.service')}</dt>
              <dd>{bookingService(opened, services, lang) || '—'}</dd>
            </div>
            {linkRow(opened, linkAsked, link) === 'button' && (
              <div className="desk-sum__wide">
                <dt>{say('hall.manageLink')}</dt>
                <dd>
                  <Button variant="secondary" size="sm" onPress={() => showLink(opened)}>
                    {say('hall.showLink')}
                  </Button>
                </dd>
              </div>
            )}
            {linkRow(opened, linkAsked, link) === 'link' && (
              <div className="desk-sum__wide">
                <dt>{say('hall.manageLink')}</dt>
                <dd>
                  {/* Shown as well as copied: a plain-HTTP desk has no clipboard. */}
                  <code className="rx-link">{link}</code>
                  {canCopy() && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onPress={() =>
                        void navigator.clipboard
                          .writeText(link)
                          .then(() => setCopied(true))
                          .catch(() => setCopied(false))
                      }
                    >
                      {say(copied ? 'common.copied' : 'hall.copyLink')}
                    </Button>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </FormModal>
      )}
      {moving && (
        <BookDrawer
          branchId={branchId}
          services={services}
          zone={zone}
          today={today}
          moving={moving}
          onClose={() => setMoving(null)}
          onDone={(a) => {
            setMoving(null)
            setOpened(null)
            setNotice(say('booking.moved', { time: formatMoment(a.startsAt, lang, { time: true, zone }) }))
            void load()
          }}
        />
      )}
    </Card>
  )
}
